import React, { useState } from 'react';
import axiosInstance from '../../axiosConfig';
import ScheduleVerificationModal from './ScheduleVerificationModal';
import DocumentUpload from './DocumentUpload';
import './MemberPortal.css';

/**
 * VerificationMethodSelection
 * - Presents verification options
 * - "Upload Proof" renders <DocumentUpload /> and sets verification_method="document"
 * - "Schedule Call" opens modal; after success sets verification_method="live_call"
 */
const VerificationMethodSelection = ({ onMethodChosen }) => {
  const [showModal, setShowModal] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);

  const closeScheduler = () => setShowModal(false);

  // PATCH helper for verification_method
  const setVerificationMethod = async (method) => {
    try {
      await axiosInstance.patch('/member-portal/my-profile/', {
        verification_method: method,
        verification_status: 'pending', // ensure status reflects the flow
      });
    } catch (e) {
      console.warn('Could not set verification_method:', e);
    }
  };

  const openUpload = async () => {
    await setVerificationMethod('document');
    setShowUpload(true);
  };

  const scheduleVerificationCall = async ({ start_time, timezone, duration_minutes }) => {
    setLoading(true);
    try {
      const payload = { start_time, timezone, duration_minutes };
      const { data } = await axiosInstance.post('/verification/schedule-call/', payload);
      console.log('Verification Call Scheduled:', data);

      // mark method AFTER successful schedule
      await setVerificationMethod('live_call');

      setToast({
        type: 'success',
        message: 'Call scheduled. Check your email for the Zoom invite & calendar attachment.',
      });
      closeScheduler();

      // Trigger refresh to show VerificationPending screen
      if (onMethodChosen) {
        onMethodChosen();
      }
    } catch (err) {
      console.error(err);
      setToast({
        type: 'error',
        message: 'We could not schedule your call. Please try a different time.',
      });
    } finally {
      setLoading(false);
    }
  };

  // Document Upload "full-screen" panel within wrapper
  if (showUpload) {
    return (
      <div className="verify-wrapper">
        <DocumentUpload
          onBack={() => setShowUpload(false)}
          onComplete={() => {
            setToast({
              type: 'success',
              message: 'Documents submitted for review. We will email you once verified.',
            });
            setShowUpload(false);

            // Trigger refresh to show VerificationPending screen
            if (onMethodChosen) {
              onMethodChosen();
            }
          }}
        />
        {toast && (
          <div
            className={`piq-toast ${toast.type === 'error' ? 'piq-toast-error' : 'piq-toast-success'}`}
            role="status"
            aria-live="polite"
          >
            {toast.message}
            <button className="piq-icon-btn" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
          </div>
        )}
      </div>
    );
  }

  // Default selection UI
  return (
    <div className="verify-wrapper">
      <h2 className="verify-title">Verify Your Club Membership</h2>
      <p className="verify-subtitle">
        Choose a verification method to access the members-only portal.
      </p>

      <div className="verify-grid">
        {/* Document upload card */}
        <div className="verify-card">
          <div className="verify-card-body">
            <div className="verify-card-emoji">📎</div>
            <h3 className="verify-card-title">Upload Proof</h3>
            <p className="verify-card-text">
              Upload a recent statement, membership card, or portal screenshot to verify membership.
            </p>
          </div>
          <div className="verify-card-actions">
            <button className="piq-btn-secondary" type="button" onClick={openUpload}>
              Upload Document
            </button>
          </div>
        </div>

        {/* Schedule Call card */}
        <div className="verify-card">
          <div className="verify-card-body">
            <div className="verify-card-emoji">📞</div>
            <h3 className="verify-card-title">Schedule Call</h3>
            <p className="verify-card-text">
              Hop on a quick Zoom to screen-share your membership proof with our team.
            </p>
          </div>
          <div className="verify-card-actions">
            <button
              className="piq-btn-primary"
              type="button"
              onClick={() => setShowModal(true)}
              disabled={loading}
            >
              {loading ? 'Opening…' : 'Schedule'}
            </button>
          </div>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div
          className={`piq-toast ${toast.type === 'error' ? 'piq-toast-error' : 'piq-toast-success'}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
          <button className="piq-icon-btn" onClick={() => setToast(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      {/* Date/Time Modal */}
      <ScheduleVerificationModal
        isOpen={showModal}
        onClose={closeScheduler}
        onConfirm={scheduleVerificationCall}
        defaultDuration={25}
      />
    </div>
  );
};

export default VerificationMethodSelection;
